/**
 * Capacity to consent to the decision under assessment. Set by a Functional and
 * Diagnostic Mental Capacity Assessment, or recorded by hand.
 */
export var CapacityOutcome;
(function (CapacityOutcome) {
    CapacityOutcome["NotAssessed"] = "notAssessed";
    CapacityOutcome["LacksCapacity"] = "lacksCapacity";
    CapacityOutcome["HasCapacity"] = "hasCapacity";
})(CapacityOutcome || (CapacityOutcome = {}));
/**
 * Whether the arrangements amount to a deprivation of liberty. Set by a
 * deprivation of liberty test, or recorded by hand.
 */
export var DeprivationOutcome;
(function (DeprivationOutcome) {
    DeprivationOutcome["NotAssessed"] = "notAssessed";
    DeprivationOutcome["NotDeprived"] = "notDeprived";
    DeprivationOutcome["Deprived"] = "deprived";
})(DeprivationOutcome || (DeprivationOutcome = {}));
/**
 * The displayed status. Never stored — always derived from the two outcomes above
 * by {@link resolveCapacityStatus}, so revising a label never migrates data.
 */
export var CapacityStatus;
(function (CapacityStatus) {
    CapacityStatus["NoStatus"] = "No Status";
    CapacityStatus["AwaitingConsentAssessment"] = "Awaiting Consent Assessment";
    CapacityStatus["AwaitingCapacityAssessment"] = "Awaiting Capacity Assessment";
    CapacityStatus["ValidlyConsenting"] = "Lacking capacity, but validly consenting";
    CapacityStatus["ConsentingWithCapacity"] = "Consenting with capacity";
    CapacityStatus["DolsApplies"] = "Lacks capacity, DoLS applies";
    CapacityStatus["SafeguardingConcern"] = "Safeguarding concern";
})(CapacityStatus || (CapacityStatus = {}));
/**
 * Progress of the DoLS application itself, from referral through to authorisation
 * or expiry. Only meaningful while the status resolves to
 * {@link CapacityStatus.DolsApplies}.
 *
 * An urgent authorisation is deliberately absent: it is care-home issued and runs
 * while a standard application is pending, so it coexists with these rather than
 * replacing one. See {@link UrgentAuthorisation}.
 */
export var DolsTrackingStatus;
(function (DolsTrackingStatus) {
    DolsTrackingStatus["ReferralNotStarted"] = "Referral not started";
    DolsTrackingStatus["ReferralInProgress"] = "Referral in progress";
    DolsTrackingStatus["StandardAuthorisationGranted"] = "Standard authorisation granted";
    DolsTrackingStatus["StandardAuthorisationExpired"] = "Standard authorisation expired";
})(DolsTrackingStatus || (DolsTrackingStatus = {}));
/**
 * Why somebody stopped being a resident. A discharge, a death and a mistake have
 * different consequences for the assessment history, so they stay distinct rather
 * than collapsing into a deletion.
 */
export var ResidencyEndReason;
(function (ResidencyEndReason) {
    ResidencyEndReason["MovedOut"] = "moved-out";
    ResidencyEndReason["Deceased"] = "deceased";
    ResidencyEndReason["RecordedInError"] = "recorded-in-error";
})(ResidencyEndReason || (ResidencyEndReason = {}));
/**
 * Capacity is decision-specific. Only `residence` counts toward the register
 * status; other decisions are recorded against the patient and shown in their
 * history without moving the pill.
 */
export var CapacityDecisionType;
(function (CapacityDecisionType) {
    CapacityDecisionType["Residence"] = "residence";
    CapacityDecisionType["Other"] = "other";
})(CapacityDecisionType || (CapacityDecisionType = {}));
/**
 * The statuses that mean "nothing substantive is recorded yet". They exist so a
 * resident can be on the register before anybody has assessed them, which is the
 * state the register exists to surface.
 */
export const UnassessedStatuses = [
    CapacityStatus.NoStatus,
    CapacityStatus.AwaitingCapacityAssessment,
    CapacityStatus.AwaitingConsentAssessment,
];
/**
 * Statuses that should read as an escalation rather than a steady state. A person
 * with capacity who is deprived of their liberty is either consenting — in which
 * case it is not a deprivation — or unlawfully detained.
 */
export const EscalationStatuses = [CapacityStatus.SafeguardingConcern];
//# sourceMappingURL=CapacityState.js.map